import React, { useEffect, useState } from "react";
import { Star, MessageCircle, Loader2 } from "lucide-react";
import api from "@/lib/api";

/**
 * StarRow — inline star rendering with half-star support via CSS clip.
 */
export function StarRow({ value = 0, max = 5, size = 16 }) {
  const full = Math.floor(value);
  const hasHalf = value - full >= 0.5;
  return (
    <div className="inline-flex items-center gap-0.5" aria-label={`${value} out of ${max} stars`}>
      {Array.from({ length: max }).map((_, i) => {
        const solid = i < full;
        const half = !solid && i === full && hasHalf;
        return (
          <Star
            key={i}
            className={`transition ${solid ? "text-amber-400 fill-amber-400" : half ? "text-amber-400" : "text-slate-300"}`}
            style={{ width: size, height: size, ...(half ? { fill: "url(#half-gradient)" } : {}) }}
          />
        );
      })}
    </div>
  );
}

/**
 * StoreRatingBadge — compact avg + count summary. Reads /api/kids/{id}/rating.
 * When there are no reviews, renders "No reviews yet" (dim, no stars).
 */
export function StoreRatingBadge({ kidId, size = 14, showEmpty = true }) {
  const [summary, setSummary] = useState({ count: 0, average: null });
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    if (!kidId) return;
    api.get(`/kids/${kidId}/rating`).then(({ data }) => {
      setSummary({ count: data?.count || 0, average: data?.average ?? null });
    }).catch(() => setSummary({ count: 0, average: null })).finally(() => setLoading(false));
  }, [kidId]);

  if (loading) {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] text-slate-400" data-testid={`store-rating-loading-${kidId}`}>
        <Loader2 className="w-3 h-3 animate-spin" /> Loading&hellip;
      </span>
    );
  }

  if (summary.count === 0) {
    if (!showEmpty) return null;
    return (
      <span className="inline-flex items-center gap-1 text-[11px] font-bold text-slate-500" data-testid={`store-rating-empty-${kidId}`}>
        <Star className="w-3 h-3" /> No reviews yet
      </span>
    );
  }

  return (
    <span className="inline-flex items-center gap-1.5 text-[12px] font-bold text-brandSlate" data-testid={`store-rating-summary-${kidId}`}>
      <StarRow value={summary.average || 0} size={size} />
      <span data-testid={`store-rating-average-${kidId}`}>{(summary.average || 0).toFixed(1)}</span>
      <span className="text-slate-500 font-normal">({summary.count} {summary.count === 1 ? "review" : "reviews"})</span>
    </span>
  );
}

/**
 * StoreReviewsSection — feed of the last 25 reviews left for the kid's
 * storefront. Public — anyone (even signed-out) can read. Individual
 * reviewer identity is trimmed to first name by the API.
 */
export function StoreReviewsSection({ kidId }) {
  const [reviews, setReviews] = useState([]);
  const [summary, setSummary] = useState({ count: 0, average: null });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!kidId) return;
    Promise.all([
      api.get(`/kids/${kidId}/reviews`),
      api.get(`/kids/${kidId}/rating`),
    ]).then(([r, s]) => {
      setReviews(r.data?.reviews || []);
      setSummary({ count: s.data?.count || 0, average: s.data?.average ?? null });
    }).catch(() => { /* silent */ }).finally(() => setLoading(false));
  }, [kidId]);

  if (loading) {
    return (
      <div className="card-base p-6 mt-12" data-testid="store-reviews-loading">
        <div className="inline-flex items-center gap-2 text-sm text-slate-500">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading reviews&hellip;
        </div>
      </div>
    );
  }

  return (
    <div className="mt-12" data-testid="store-reviews-section">
      <div className="flex items-end justify-between gap-3 mb-4 flex-wrap">
        <div>
          <div className="pill bg-brandTeal/15 text-brandTeal" data-testid="store-reviews-pill"><Star className="w-3 h-3 mr-1 fill-current" /> Star Rating &amp; Reviews</div>
          <h2 className="font-display text-2xl md:text-3xl font-semibold text-brandSlate mt-2" data-testid="store-reviews-heading">
            Star Rating &amp; Reviews
          </h2>
        </div>
        {summary.count > 0 && (
          <div className="text-right">
            <div className="inline-flex items-center gap-2">
              <StarRow value={summary.average || 0} size={22} />
              <span className="font-display text-2xl font-semibold text-brandSlate" data-testid="store-reviews-average">
                {(summary.average || 0).toFixed(1)}
              </span>
            </div>
            <div className="text-xs text-slate-500 mt-0.5" data-testid="store-reviews-count">
              {summary.count} {summary.count === 1 ? "review" : "reviews"}
            </div>
          </div>
        )}
      </div>

      {reviews.length === 0 ? (
        <div className="card-base p-8 text-center" data-testid="store-reviews-empty">
          <MessageCircle className="w-8 h-8 text-slate-400 mx-auto" />
          <div className="font-bold text-slate-700 mt-3">No reviews yet</div>
          <p className="text-sm text-slate-500 mt-1">Be the first to buy a toy and share your thoughts.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="store-reviews-grid">
          {reviews.map((r) => (
            <div key={r.id} className="card-base p-5" data-testid={`store-review-${r.id}`}>
              <div className="flex items-center justify-between gap-2">
                <div className="font-bold text-brandSlate">{r.reviewer_name || "A buyer"}</div>
                <StarRow value={r.rating || 0} size={16} />
              </div>
              {r.note && (
                <p className="text-sm text-slate-700 mt-2 leading-relaxed break-words">
                  &ldquo;{r.note}&rdquo;
                </p>
              )}
              <div className="text-[11px] text-slate-400 mt-2">
                {r.created_at ? new Date(r.created_at).toLocaleDateString("en-GB", { year: "numeric", month: "short", day: "numeric" }) : ""}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
